import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface MemoryType {
  name: string;
  english: string;
  duration: string;
  capacity: string;
  icon: string;
  color: string;
}

interface MemoryTechnique {
  name: string;
  english: string;
  desc: string;
  icon: string;
  color: string;
}

interface LearningMethod {
  name: string;
  desc: string;
  icon: string;
}

interface MemoryTechTemplateProps {
  topic: string;
  subtitle: string;
  memoryTypes: MemoryType[];
  techniques: MemoryTechnique[];
  learningMethods: LearningMethod[];
  forgettingCurveData: { hours: number; retention: number }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: MemoryTechTemplateProps = {
  topic: '记忆术与高效学习',
  subtitle: 'Memory Techniques & Efficient Learning',
  memoryTypes: [
    {
      name: '感觉记忆',
      english: 'Sensory',
      duration: '0.25-3秒',
      capacity: '极大',
      icon: '👁️',
      color: '#c084fc',
    },
    {
      name: '短期记忆',
      english: 'Short-term',
      duration: '15-30秒',
      capacity: '7±2项',
      icon: '⏱️',
      color: '#a78bfa',
    },
    {
      name: '工作记忆',
      english: 'Working',
      duration: '操作中',
      capacity: '4组块',
      icon: '🧮',
      color: '#818cf8',
    },
    {
      name: '长期记忆',
      english: 'Long-term',
      duration: '永久',
      capacity: '无限',
      icon: '🏛️',
      color: '#6d28d9',
    },
  ],
  techniques: [
    {
      name: '记忆宫殿',
      english: 'Memory Palace',
      desc: '将信息与熟悉空间位置关联',
      icon: '🏰',
      color: '#c084fc',
    },
    {
      name: '间隔重复',
      english: 'Spaced Repetition',
      desc: '按遗忘曲线规律安排复习',
      icon: '📅',
      color: '#a78bfa',
    },
    {
      name: '联想法',
      english: 'Association',
      desc: '创建生动奇特的心理画面',
      icon: '🔗',
      color: '#818cf8',
    },
    {
      name: '思维导图',
      english: 'Mind Mapping',
      desc: '以放射状结构组织知识',
      icon: '🗺️',
      color: '#6d28d9',
    },
  ],
  learningMethods: [
    { name: '费曼技巧', desc: '用简单语言向他人解释', icon: '👨‍🏫' },
    { name: '番茄工作法', desc: '25分钟专注+5分钟休息', icon: '🍅' },
    { name: '主动回忆', desc: '不看笔记尝试回忆内容', icon: '🧠' },
    { name: '交错练习', desc: '混合不同类型的练习题', icon: '🔀' },
  ],
  forgettingCurveData: [
    { hours: 0, retention: 100 },
    { hours: 1, retention: 44 },
    { hours: 8, retention: 36 },
    { hours: 24, retention: 33 },
    { hours: 48, retention: 28 },
    { hours: 168, retention: 25 },
    { hours: 720, retention: 21 },
  ],
  stats: [
    { value: '7±2', label: '短期记忆容量(米勒定律)' },
    { value: '56%', label: '一小时后遗忘的内容' },
    { value: '10x', label: '间隔重复vs集中学习效率比' },
  ],
  bottomQuote:
    '记忆不是天赋，而是一项可以训练的技能——世界记忆冠军的大脑结构与普通人并无不同',
};

export const MemoryTechTemplate: React.FC<
  Partial<MemoryTechTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const typeStart = 30;
  const techStart = 160;
  const curveStart = 200;
  const methodStart = 350;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const curveDraw = interpolate(frame, [curveStart, curveStart + 100], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const curvePoints = p.forgettingCurveData.map((d, i) => {
    const x = 60 + (i / (p.forgettingCurveData.length - 1)) * 700;
    const y = 250 - (d.retention / 100) * 210;
    return { x, y, ...d };
  });
  const curvePath = curvePoints
    .map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x},${pt.y}`)
    .join(' ');

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #1a0a2e 0%, #0f0720 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating brain/neuron particles */}
      {Array.from({ length: 50 }).map((_, i) => {
        const x = (i * 401) % 1920;
        const y = (i * 197) % 1080;
        const size = 3 + (i % 5);
        const drift = Math.sin(frame * 0.03 + i * 1.2) * 10;
        const driftX = Math.cos(frame * 0.025 + i * 0.9) * 7;
        const pulse = 0.06 + 0.05 * Math.sin(frame * 0.04 + i * 0.7);
        const colors = ['#c084fc', '#a78bfa', '#818cf8', '#6d28d9'];
        const isNeuron = i % 4 === 0;
        return isNeuron ? (
          <svg
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x + driftX,
              top: y + drift,
              opacity: pulse,
            }}
            width={size * 6}
            height={size * 6}
            viewBox="0 0 30 30"
          >
            <circle cx="15" cy="15" r="4" fill={colors[i % colors.length]} />
            {[0, 60, 120, 180, 240, 300].map((angle, j) => {
              const rad = (angle * Math.PI) / 180;
              return (
                <line
                  key={j}
                  x1="15"
                  y1="15"
                  x2={15 + 10 * Math.cos(rad)}
                  y2={15 + 10 * Math.sin(rad)}
                  stroke={colors[i % colors.length]}
                  strokeWidth="1"
                  opacity="0.5"
                />
              );
            })}
          </svg>
        ) : (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x + driftX,
              top: y + drift,
              width: size,
              height: size,
              borderRadius: '50%',
              background: colors[i % colors.length],
              opacity: pulse,
              boxShadow: `0 0 ${size * 3}px ${colors[i % colors.length]}40`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#6d28d9',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#c084fc',
            flexShrink: 0,
          }}
        >
          🧠 记忆科学
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#ede9fe',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 24,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Memory Types ===== */}
      <div style={{ position: 'absolute', top: 120, left: 36, width: 460 }}>
        <div
          style={{
            fontSize: 24,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          记忆类型
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.memoryTypes.map((mt, i) => {
            const s = spring({
              frame: frame - (typeStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#150a28',
                  borderRadius: 14,
                  padding: '12px 16px',
                  border: `1.5px solid ${mt.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: `${mt.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 28,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${mt.color}30`,
                  }}
                >
                  {mt.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}
                  >
                    <span
                      style={{ fontSize: 20, fontWeight: 800, color: mt.color }}
                    >
                      {mt.name}
                    </span>
                    <span style={{ fontSize: 20, color: '#64748b' }}>
                      {mt.english}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: 16,
                      marginTop: 2,
                    }}
                  >
                    <span style={{ fontSize: 18, color: '#94a3b8' }}>
                      持续: {mt.duration}
                    </span>
                    <span style={{ fontSize: 18, color: '#94a3b8' }}>
                      容量: {mt.capacity}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Techniques */}
        <div style={{ marginTop: 18 }}>
          <div
            style={{
              fontSize: 24,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            记忆术
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {p.techniques.map((t, i) => {
              const s = spring({
                frame: frame - (techStart + i * 16),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: '#150a28',
                    borderRadius: 12,
                    padding: '10px 14px',
                    border: `1.5px solid ${t.color}30`,
                    opacity: s,
                    transform: `translateX(${(1 - s) * 24}px)`,
                  }}
                >
                  <div style={{ fontSize: 24, flexShrink: 0 }}>{t.icon}</div>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 8,
                      }}
                    >
                      <span
                        style={{ fontSize: 20, fontWeight: 800, color: t.color }}
                      >
                        {t.name}
                      </span>
                      <span style={{ fontSize: 20, color: '#64748b' }}>
                        {t.english}
                      </span>
                    </div>
                    <div style={{ fontSize: 18, color: '#94a3b8', marginTop: 1 }}>
                      {t.desc}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== CENTER: Ebbinghaus Forgetting Curve SVG ===== */}
      <div style={{ position: 'absolute', top: 150, left: 680, width: 720 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          艾宾浩斯遗忘曲线
        </div>
        <svg width="680" height="320" viewBox="0 0 800 280">
          {/* Grid lines */}
          {[55, 107, 160, 212].map((y) => (
            <line
              key={y}
              x1="60"
              y1={y}
              x2="780"
              y2={y}
              stroke="#2d1a4e"
              strokeWidth="1"
              opacity="0.3"
            />
          ))}

          {/* Y axis labels */}
          <text x="-4" y="48" fill="#64748b" fontSize="22">100%</text>
          <text x="5" y="100" fill="#64748b" fontSize="22">75%</text>
          <text x="5" y="152" fill="#64748b" fontSize="22">50%</text>
          <text x="5" y="205" fill="#64748b" fontSize="22">25%</text>
          <text x="5" y="258" fill="#64748b" fontSize="22">0%</text>

          {/* X axis labels */}
          <text x="60" y="275" fill="#64748b" fontSize="22" textAnchor="middle">0h</text>
          <text x="177" y="275" fill="#64748b" fontSize="22" textAnchor="middle">1h</text>
          <text x="294" y="275" fill="#64748b" fontSize="22" textAnchor="middle">8h</text>
          <text x="410" y="275" fill="#64748b" fontSize="22" textAnchor="middle">24h</text>
          <text x="527" y="275" fill="#64748b" fontSize="22" textAnchor="middle">48h</text>
          <text x="643" y="275" fill="#64748b" fontSize="22" textAnchor="middle">7d</text>
          <text x="760" y="275" fill="#64748b" fontSize="22" textAnchor="middle">30d</text>

          {/* Forgetting curve */}
          <path
            d={curvePath}
            fill="none"
            stroke="#c084fc"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="1200"
            strokeDashoffset={1200 * (1 - curveDraw)}
          />

          {/* Area under curve */}
          <path
            d={`${curvePath} L${curvePoints[curvePoints.length - 1].x},250 L60,250 Z`}
            fill="#c084fc"
            opacity={0.08 * curveDraw}
          />

          {/* Data points */}
          {curvePoints.map((pt, i) => {
            const dotS = spring({
              frame: frame - (curveStart + 30 + i * 10),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <g key={i} opacity={dotS}>
                <circle cx={pt.x} cy={pt.y} r="7" fill="#c084fc" stroke="#ede9fe" strokeWidth="2" />
                <text
                  x={pt.x}
                  y={pt.y - 16}
                  fill="#ede9fe"
                  fontSize="20"
                  fontWeight="700"
                  textAnchor="middle"
                >
                  {pt.retention}%
                </text>
              </g>
            );
          })}

          {/* Spaced repetition boost lines */}
          <g
            opacity={interpolate(frame, [curveStart + 70, curveStart + 90], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          >
            <path d="M177,132 Q250,60 350,73" fill="none" stroke="#34d399" strokeWidth="2" strokeDasharray="6 3" />
            <path d="M350,73 Q450,40 550,53" fill="none" stroke="#34d399" strokeWidth="2" strokeDasharray="6 3" />
            <text x="400" y="35" fill="#34d399" fontSize="20" fontWeight="700" textAnchor="middle">
              间隔复习后的记忆恢复
            </text>
          </g>
        </svg>

        {/* Learning Methods */}
        <div style={{ marginTop: 18 }}>
          <div
            style={{
              fontSize: 24,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            高效学习法
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.learningMethods.map((m, i) => {
              const s = spring({
                frame: frame - (methodStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#150a28',
                    borderRadius: 12,
                    padding: '14px 12px',
                    border: '1.5px solid #6d28d930',
                    opacity: s,
                    transform: `translateY(${(1 - s) * 16}px)`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <div style={{ fontSize: 28, flexShrink: 0 }}>{m.icon}</div>
                  <div>
                    <div
                      style={{
                        fontSize: 19,
                        fontWeight: 800,
                        color: '#c084fc',
                      }}
                    >
                      {m.name}
                    </div>
                    <div style={{ fontSize: 18, color: '#94a3b8', marginTop: 1 }}>
                      {m.desc}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== RIGHT COLUMN: Memory Tips ===== */}
      <div style={{ position: 'absolute', top: 110, right: 36, width: 380 }}>
        <div
          style={{
            fontSize: 24,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          记忆增强要素
        </div>
        {[
          { name: '睡眠巩固', desc: '深度睡眠中大脑整理并固化白天记忆', icon: '😴', color: '#818cf8' },
          { name: '情绪编码', desc: '情感相关的记忆更容易被长期保存', icon: '❤️', color: '#f472b6' },
          { name: '多感官输入', desc: '视听触多通道编码增强记忆痕迹', icon: '🎭', color: '#c084fc' },
          { name: '运动促进', desc: '有氧运动促进海马体神经元新生', icon: '🏃', color: '#34d399' },
        ].map((item, i) => {
          const s = spring({
            frame: frame - (methodStart + 60 + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#150a28',
                borderRadius: 12,
                padding: '14px 16px',
                border: `1.5px solid ${item.color}30`,
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 10,
                  background: `${item.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: item.color }}>
                  {item.name}
                </div>
                <div style={{ fontSize: 18, color: '#94a3b8', marginTop: 2 }}>
                  {item.desc}
                </div>
              </div>
            </div>
          );
        })}

        {/* Brain Facts */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {[
              { label: '860亿神经元', icon: '🧬' },
              { label: '100万亿突触', icon: '⚡' },
              { label: '海马体可塑性', icon: '🌊' },
              { label: '神经可塑性', icon: '🔧' },
            ].map((item, i) => {
              const s = spring({
                frame: frame - (methodStart + 120 + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#150a28',
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    border: '1px solid #6d28d930',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ fontSize: 26, marginBottom: 4 }}>{item.icon}</div>
                  <div style={{ fontSize: 19, fontWeight: 700, color: '#a78bfa' }}>
                    {item.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 90,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#150a28',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #6d28d930',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 10 }}>
                <span style={{ fontSize: 34, fontWeight: 900, color: '#c084fc' }}>
                  {st.value}
                </span>
                <span style={{ fontSize: 18, color: '#94a3b8' }}>
                  {st.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 18,
          left: 36,
          right: 36,
          background: '#150a28',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #6d28d9',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🧠</span>
          <span style={{ fontSize: 19, color: '#ede9fe', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #6d28d9, #a78bfa, #ede9fe)',
        }}
      />
    </AbsoluteFill>
  );
};
